import React, { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";

// Shows a bottom-sheet "Install Members Day" nudge on every mobile browser visit.
// - Android/Chrome: taps "Install" → native Add-to-Home-Screen prompt via beforeinstallprompt.
// - iOS Safari: no API, so we show the "Share → Add to Home Screen" instruction illustrated.
// - Hidden entirely when already running standalone (display-mode) or on desktop.
export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const uaMobile = /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    // Fallback signals so emulators, in-app browsers and touch laptops still see the prompt.
    const touch = (navigator.maxTouchPoints || 0) > 0;
    const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.matchMedia && window.matchMedia("(max-width: 820px)").matches;
    const isMobile = uaMobile || ((touch || coarse) && narrow);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (!isMobile || standalone) return;
    setIsIOS(/iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua));
    // Show the sheet on every page load (user asked for this behaviour until the event).
    const t = setTimeout(() => setVisible(true), 900); // small delay so it doesn't fight the splash
    const onBeforeInstall = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div data-testid="install-prompt" style={styles.wrap} role="dialog" aria-live="polite">
      <div style={styles.card}>
        <button data-testid="install-prompt-close" onClick={() => setVisible(false)} aria-label="Close" style={styles.close}>
          <X size={16} />
        </button>
        <div style={styles.row}>
          <img src="/icons/android-chrome-192.png" alt="" width={56} height={56} style={styles.icon} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.title}>Install Members Day</div>
            <div style={styles.sub}>
              We recommend installing the app before Members Day — one-tap login after you register, no browser hassle.
            </div>
          </div>
        </div>

        {isIOS ? (
          <div style={styles.iosHelp} data-testid="install-prompt-ios-steps">
            <div style={styles.iosStep}>
              <Share size={14} style={{ color: "#C73C44" }} />
              <span>Tap <b>Share</b> in Safari's bottom bar</span>
            </div>
            <div style={styles.iosStep}>
              <Plus size={14} style={{ color: "#C73C44" }} />
              <span>Choose <b>Add to Home Screen</b></span>
            </div>
          </div>
        ) : (
          <button
            data-testid="install-prompt-install"
            onClick={install}
            disabled={!deferred}
            style={{ ...styles.cta, opacity: deferred ? 1 : 0.55 }}
          >
            <Download size={14} /> {deferred ? "Install app" : "Preparing install…"}
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9000, padding: "0 10px calc(env(safe-area-inset-bottom, 0px) + 10px)", pointerEvents: "none" },
  card: { pointerEvents: "auto", background: "#0F1115", border: "1px solid rgba(199,60,68,0.35)", borderRadius: 16, padding: "14px 14px 12px", boxShadow: "0 18px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.03) inset", color: "#F7F4EE", fontFamily: "Inter, system-ui, sans-serif", position: "relative", maxWidth: 520, margin: "0 auto" },
  close: { position: "absolute", top: 8, right: 8, background: "transparent", border: "none", color: "#B8B0A0", padding: 6, cursor: "pointer", borderRadius: 999 },
  row: { display: "flex", gap: 12, alignItems: "flex-start", paddingRight: 24 },
  icon: { borderRadius: 12, flexShrink: 0, boxShadow: "0 4px 12px rgba(199,60,68,0.25)" },
  title: { fontWeight: 700, fontSize: 15, letterSpacing: "0.01em" },
  sub: { fontSize: 12.5, lineHeight: 1.45, color: "#B8B0A0", marginTop: 4 },
  cta: { marginTop: 12, width: "100%", background: "linear-gradient(180deg,#C73C44,#9A1B23)", color: "#fff", border: "none", borderRadius: 999, padding: "11px 14px", fontWeight: 600, fontSize: 13.5, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" },
  iosHelp: { marginTop: 12, background: "rgba(199,60,68,0.08)", border: "1px solid rgba(199,60,68,0.25)", borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 },
  iosStep: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#F7F4EE" },
};
